import * as React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { arrayRemoveAll, formValueSelector } from 'redux-form'
// components
import { ReduxSelect } from 'components/FormElements/ReduxForm'
// view components
import Action from './components/Action'
import SendEmail from './components/SendEmail'
import CreateCase from './components/CreateCase'
import UpdateCase from './components/UpdateCase'
import MakePrivate from './components/MakePrivate'
import SetProperty from './components/SetProperty'
import RelatedForm from './components/RelatedForm'
import ShowFunction from './components/ShowFunction'
import Confirmation from './components/Confirmation'
import RestrictRecord from './components/RestrictRecord'
import CheckMandatory from './components/CheckMandatory'
import CreateSchedule from './components/CreateSchedule'
import ResponseMessage from './components/ResponseMessage'
import CreateParentCase from './components/CreateParentCase'
import UpdateParentCase from './components/UpdateParentCase'
import SetReviewApprover from './components/SetReviewApprover'
import EnableGroupAccess from './components/EnableGroupAccess'
import CreateRelatedForm from './components/CreateRelatedForm'
import ShowInlineFunction from './components/ShowInlineFunction'
import SupervisorHierarchy from './components/SupervisorHierarchy'
import AccessFromRelatedForm from './components/AccessFromRelatedForm'
import ScheduleTemplateSelect from './components/ScheduleTemplateSelect'
// constants
import { Forms, RISK_TRANSACTION_TYPE_OPTIONS, TRANSACTION_TYPE_OPTIONS } from 'constants/index'
// views
import { CloseIcon } from 'components/Modals/SystemProcessModal/Form/components/views'
import { FieldWrapper, Wrapper } from './views'
// types
import { IOption } from 'components/FormElements/Select'
import { SystemProcessTransactionType, SystemProcessType } from 'types/graphql'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// utils
import { required } from 'utils/validators'

interface IIncomingProps {
  field: string
  removeField: () => void
  index: number
}

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

// Offered only once Injury Master is connected
const INJURY_MASTER_TRANSACTION_TYPES = [
  SystemProcessTransactionType.CreateCase,
  SystemProcessTransactionType.UpdateCase,
  SystemProcessTransactionType.CreateParentCase,
  SystemProcessTransactionType.UpdateParentCase
]

const Transaction: React.FC<IIncomingProps> = ({ field, removeField, index }) => {
  const dispatch = useDispatch()

  const { isRiskTemplate, isInjuryMasterConfigured } = React.useContext(RiskTemplateAPIContext)

  const { transactionType, form, processType } = useSelector((state) => ({
    form: selector(state, `${field}.form.value`),
    processType: selector(state, 'processType'),
    transactionType: selector(state, `${field}.transactionType.value`)
  }))

  const transactionTypeOptions = React.useMemo(() => {
    const options = isRiskTemplate
      ? processType?.value === SystemProcessType.Automation
        ? RISK_TRANSACTION_TYPE_OPTIONS.slice(0, 2)
        : RISK_TRANSACTION_TYPE_OPTIONS
      : TRANSACTION_TYPE_OPTIONS

    return isInjuryMasterConfigured
      ? options
      : options.filter((option) => !INJURY_MASTER_TRANSACTION_TYPES.includes(option.value))
  }, [isRiskTemplate, processType, isInjuryMasterConfigured])

  // The case types share propertyMappings, so a switch would carry over e.g. the required rows a
  // create adds to an update — each type starts from its own defaults instead.
  const onTransactionTypeChange = (option: IOption) => {
    if (option?.value !== transactionType) {
      dispatch(arrayRemoveAll(Forms.SYSTEM_PROCESS_CREATE_FORM, `${field}.propertyMappings`))
    }
  }

  return (
    <Wrapper data-testid='system-process-transaction' data-entity-id={index}>
      <CloseIcon
        data-testid='system-process-remove-transaction'
        data-entity-id={index}
        onClick={removeField}
        iconSize={24}
      />
      <FieldWrapper width={'50%'}>
        <ReduxSelect
          validate={required}
          menuPortalTarget={null}
          label={'Transaction Type'}
          options={transactionTypeOptions}
          name={`${field}.transactionType`}
          onSelectChange={onTransactionTypeChange}
        />
      </FieldWrapper>

      {transactionType === SystemProcessTransactionType.ShowFunction && <ShowFunction field={field} />}
      {transactionType === SystemProcessTransactionType.ShowInlineFunction && <ShowInlineFunction field={field} />}
      {transactionType === SystemProcessTransactionType.SetField && <SetProperty field={field} />}
      {transactionType === SystemProcessTransactionType.ShowRelatedForm && <RelatedForm field={field} form={form} />}
      {transactionType === SystemProcessTransactionType.SendEmail && <SendEmail field={field} />}
      {transactionType === SystemProcessTransactionType.CreateAction && <Action field={field} />}
      {transactionType === SystemProcessTransactionType.CreateRiskRecurrence && <CreateSchedule field={field} />}
      {transactionType === SystemProcessTransactionType.DisableRiskRecurrence && (
        <ScheduleTemplateSelect field={field} />
      )}
      {transactionType === SystemProcessTransactionType.EnableRiskRecurrence && (
        <ScheduleTemplateSelect field={field} />
      )}
      {transactionType === SystemProcessTransactionType.Confirmation && <Confirmation field={field} />}
      {transactionType === SystemProcessTransactionType.ResponseMessage && <ResponseMessage field={field} />}
      {transactionType === SystemProcessTransactionType.CheckMandatory && <CheckMandatory field={field} />}
      {transactionType === SystemProcessTransactionType.Restrict && <RestrictRecord field={field} />}
      {transactionType === SystemProcessTransactionType.EnableGroupAccess && <EnableGroupAccess field={field} />}
      {transactionType === SystemProcessTransactionType.EnableSupervisorHierarchy && (
        <SupervisorHierarchy field={field} />
      )}
      {transactionType === SystemProcessTransactionType.RemoveGuestAccess && <MakePrivate field={field} />}
      {transactionType === SystemProcessTransactionType.AccessFromRelatedForm && (
        <AccessFromRelatedForm field={field} />
      )}
      {transactionType === SystemProcessTransactionType.CreateRelatedForm && <CreateRelatedForm field={field} />}
      {transactionType === SystemProcessTransactionType.SetReviewApprover && <SetReviewApprover field={field} />}
      {transactionType === SystemProcessTransactionType.CreateCase && <CreateCase field={field} />}
      {transactionType === SystemProcessTransactionType.UpdateCase && <UpdateCase field={field} />}
      {transactionType === SystemProcessTransactionType.CreateParentCase && <CreateParentCase field={field} />}
      {transactionType === SystemProcessTransactionType.UpdateParentCase && <UpdateParentCase field={field} />}
    </Wrapper>
  )
}

export default Transaction
